/**
 * CreatorPage — `/creators/:pubkey`
 *
 * Public profile for a GI creator. Shows their published products via
 * MarketCard and, when the signed-in user's pubkey matches, their
 * earnings summary.
 */

import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Card } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingState';
import { api } from '@/api/client';
import type { ProductListing } from '@/api/types';

export function CreatorPage() {
  const { pubkey = '' } = useParams<{ pubkey: string }>();

  const query = useQuery({
    queryKey: ['creator', pubkey],
    queryFn: () => api.getCreatorProfile(pubkey),
    enabled: pubkey.length > 0,
  });

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-2xl">
        <ErrorState
          title="Creator not found"
          body={`No creator with pubkey "${pubkey.slice(0, 16)}…" exists.`}
          actions={
            <Link to="/explore" className="contents">
              <button
                type="button"
                className="rounded-md border border-border bg-surface px-4 py-2 text-sm font-medium text-ink hover:border-border-strong"
              >
                Back to marketplace
              </button>
            </Link>
          }
        />
      </div>
    );
  }

  const profile = query.data;
  const truncatedPubkey = `${pubkey.slice(0, 8)}...${pubkey.slice(-8)}`;

  // Earnings are shown only when the signed-in user's email matches
  // the creator. Since the pubkey is the canonical identity and the
  // auth store carries the Supabase user (not a raw pubkey), we use
  // the presence of earnings data in the server response as the gate —
  // the server only includes it when the JWT subject matches the creator.
  const showEarnings = Boolean(profile.earnings);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <Link to="/explore" className="text-sm text-ink-muted hover:text-ink">
        Back to marketplace
      </Link>

      {/* Header */}
      <header className="flex flex-col gap-2 rounded-xl border border-border bg-surface p-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-xl font-semibold text-ink">
            Creator: {truncatedPubkey}
          </h1>
          <span className="inline-flex items-center rounded-full border border-spark/30 bg-spark/10 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-spark">
            Verified GI Creator
          </span>
        </div>
        {profile.display_name ? (
          <p className="text-sm text-ink-muted">{profile.display_name}</p>
        ) : null}
        <p className="text-xs text-ink-subtle">
          {profile.total_products} published{' '}
          {profile.total_products === 1 ? 'product' : 'products'}
        </p>
      </header>

      {/* Products grid */}
      <section>
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-ink-subtle">
          Products
        </h2>
        {profile.products.length === 0 ? (
          <Card>
            <p className="text-sm text-ink-muted">No published products yet.</p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {profile.products.map((p) => (
              <ProductCard key={p.listing_id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* Earnings — shown only when the server included the data */}
      {showEarnings && profile.earnings ? (
        <section>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-ink-subtle">
            Earnings
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <EarningsKpi
              label="MRR"
              value={`$${(profile.earnings.mrr_usd_cents / 100).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}`}
            />
            <EarningsKpi
              label="Total revenue"
              value={`$${(profile.earnings.total_revenue_usd_cents / 100).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}`}
            />
            <EarningsKpi
              label="Royalty payouts"
              value={String(profile.earnings.royalty_payouts_count)}
            />
          </div>
        </section>
      ) : null}

    </div>
  );
}

function ProductCard({ product }: { product: ProductListing }) {
  return (
    <Link
      to={`/products/${product.slug}`}
      className="group flex flex-col rounded-lg border border-border bg-surface p-5 shadow-subtle transition-all hover:-translate-y-0.5 hover:border-border-strong hover:shadow-raised"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
          {product.domain}
        </span>
      </div>
      <h3 className="mt-3 text-base font-semibold leading-tight text-ink">{product.name}</h3>
      <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{product.tagline}</p>
      <div className="mt-4 flex items-end justify-between border-t border-border pt-3">
        <span className="text-sm font-semibold text-ink">
          ${(product.price_usd_cents / 100).toFixed(2)}
          <span className="ms-1 text-xs font-normal text-ink-muted">/mo</span>
        </span>
        <span className="text-xs text-ink-subtle">{product.installs} installs</span>
      </div>
    </Link>
  );
}

function EarningsKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-ink">{value}</div>
    </div>
  );
}
